import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["toggle"];
  static values = {
    indeterminate: { type: Boolean, default: true },
  };

  connect() {
    this.updateState();
    this.element.addEventListener("change", this.handleCheckboxChange);
  }

  disconnect() {
    this.element.removeEventListener("change", this.handleCheckboxChange);
  }

  // Handle toggle checkbox change
  toggle(event) {
    const isChecked = event.target.checked;
    const controlledCheckboxes = this.getControlledCheckboxes(event.target);

    controlledCheckboxes.forEach((checkbox) => {
      checkbox.checked = isChecked;
      // Trigger change event for cascading updates
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  // Event delegation handler for all checkbox changes
  handleCheckboxChange = (event) => {
    const checkbox = event.target;

    // Skip if this is a toggle checkbox (handled by toggle method)
    if (this.toggleTargets.includes(checkbox)) {
      return;
    }

    // Skip if not a checkbox
    if (checkbox.type !== "checkbox") {
      return;
    }

    // Update parent toggles efficiently
    this.updateParentToggles(checkbox);
  };

  // Update only the toggles that control this specific checkbox
  updateParentToggles(checkbox) {
    this.toggleTargets.forEach((toggle) => {
      const controlledCheckboxes = this.getControlledCheckboxes(toggle);

      // Only update if this toggle controls the changed checkbox
      if (controlledCheckboxes.includes(checkbox)) {
        this.updateToggleState(toggle, controlledCheckboxes);
      }
    });
  }

  // Update all toggle states (used on connect and after toggle actions)
  updateState() {
    this.toggleTargets.forEach((toggle) => {
      const controlledCheckboxes = this.getControlledCheckboxes(toggle);
      this.updateToggleState(toggle, controlledCheckboxes);
    });
  }

  // Update a specific toggle state
  updateToggleState(toggle, controlledCheckboxes = null) {
    if (!controlledCheckboxes) {
      controlledCheckboxes = this.getControlledCheckboxes(toggle);
    }

    if (controlledCheckboxes.length === 0) return;

    const checkedCount = controlledCheckboxes.filter((cb) => cb.checked).length;
    const totalCount = controlledCheckboxes.length;

    if (checkedCount === 0) {
      toggle.checked = false;
      toggle.indeterminate = false;
    } else if (checkedCount === totalCount) {
      toggle.checked = true;
      toggle.indeterminate = false;
    } else {
      toggle.checked = false;
      toggle.indeterminate = this.indeterminateValue;
    }
  }

  // Get checkboxes controlled by a specific toggle
  getControlledCheckboxes(toggle) {
    const selector = toggle.dataset.bulkToggleSelector;

    if (selector) {
      // Use CSS selector to find controlled checkboxes
      return Array.from(this.element.querySelectorAll(selector));
    } else {
      // Fallback: find all checkboxes in the same scope, excluding other toggles
      const scope =
        toggle.closest("fieldset") || toggle.closest("form") || this.element;
      return Array.from(
        scope.querySelectorAll('input[type="checkbox"]'),
      ).filter((cb) => cb !== toggle && !this.toggleTargets.includes(cb));
    }
  }

  // Programmatic methods
  checkAll() {
    this.setAllState(true);
  }

  uncheckAll() {
    this.setAllState(false);
  }

  setAllState(checked) {
    this.toggleTargets.forEach((toggle) => {
      const controlledCheckboxes = this.getControlledCheckboxes(toggle);

      controlledCheckboxes.forEach((checkbox) => {
        checkbox.checked = checked;
      });

      this.updateToggleState(toggle, controlledCheckboxes);
    });
  }

  // Utility method to get selection state for external use
  getSelectionState() {
    const allCheckboxes = Array.from(
      this.element.querySelectorAll('input[type="checkbox"]'),
    ).filter((cb) => !this.toggleTargets.includes(cb));

    const checkedCount = allCheckboxes.filter((cb) => cb.checked).length;
    const totalCount = allCheckboxes.length;

    return {
      checked: checkedCount,
      total: totalCount,
      allChecked: checkedCount === totalCount,
      noneChecked: checkedCount === 0,
      someChecked: checkedCount > 0 && checkedCount < totalCount,
    };
  }
}
